import qs, { ParsedUrlQuery } from 'querystring';

import { DRAW_MODAL } from 'src/reducers/modal/types';
import {
  ADDED_CONSENT,
  Consent,
  LOADED_CONSENTS,
  LOADED_PENDING_CONSENTS,
  Partner,
  PartnerState,
  PendingConsent,
  REMOVED_CONSENT,
  REMOVED_PENDING_CONSENT,
  UPDATED_CONSENT,
} from 'src/reducers/partner/types';
import {
  REMOVE_SHARECODE,
  REMOVE_YEAR_OF_BIRTH,
  SET_SHARECODE,
  SET_YEAR_OF_BIRTH,
} from 'src/reducers/user/types';

import { urls } from 'Utilities/dataHelpers';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { observeStore, store } from 'Utilities/store';

import { ERROR_PARTNERS_CONNECTIONS } from 'Constants/errorTypes';
import {
  CONSENT_INFO_MODAL,
  ERROR_LOADING_MODAL,
  REMOVE_CONSENT_MODAL,
} from 'Constants/modalTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';

const handleGetConsents = () => {
  return new Promise((resolve, reject) => {
    mediator
      .request<{ consents: Consent[]; partners: Partner[]; pendingConsents: PendingConsent[] }>(
        'api:invoke',
        {
          method: 'GET',
          url: urls.lslApi + 'sharing/partner/consents/llw',
        }
      )
      .then((result) => {
        if (result.status === 0) {
          mediator.publish('partner:checkConnections', {
            consents: result.data.consents,
            partners: result.data.partners,
          });

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject();
      });
  });
};

const handleGetPendingConsents = () => {
  return new Promise((resolve, reject) => {
    mediator
      .request<{ pendingConsents: PendingConsent[] }>('api:invoke', {
        method: 'GET',
        url: urls.lslApi + 'sharing/partner/pending/llw',
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({
            type: LOADED_PENDING_CONSENTS,
            pendingConsents: result.data.pendingConsents,
          });

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject();
      });
  });
};

const handleRemoveConsent = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'DELETE',
        url: urls.lslApi + 'sharing/partner/consent/' + request.partnerId,
      })
      .then((result) => {
        store.dispatch({ type: REMOVED_CONSENT, partnerId: request.partnerId });
        resolve(result);
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

const handleRemoveConsentModal = (request: { partner: Partner }) => {
  const callback = (result: LVApiResponse) => {
    if (result.status === 0) {
      const state = store.getState();
      const partners = state.partner.partners;
      const connections = state.partner.connected;
      const pendingConnections = state.partner.pendingConnections;

      mediator.publish('partner:navigateOtherConnections', {
        partners,
        connected: connections,
        pending: pendingConnections,
      });
    }
  };

  let name = '';

  switch (request.partner.type) {
    default:
      name = request.partner.name;
      break;
  }

  let rn: boolean | undefined = false;

  switch (request.partner.type) {
    case 'api':
      rn = request.partner.rn;
      break;
    default:
      rn = false;
      break;
  }

  store.dispatch({
    type: DRAW_MODAL,
    modal: REMOVE_CONSENT_MODAL,
    props: {
      callback,
      partnerId: request.partner.id,
      partnerName: name,
      partnerRN: rn,
    },
  });
};

const handleConsentInfo = (request: any) => {
  store.dispatch({
    type: DRAW_MODAL,
    modal: CONSENT_INFO_MODAL,
    props: { scope: request.scope, scopeDetails: request.scopeDetails },
  });
};

const handleLoadingError = () => {
  store.dispatch({
    type: DRAW_MODAL,
    modal: ERROR_LOADING_MODAL,
  });
};

const handleCheckConnections = (request: any) => {
  const consents: Consent[] = request.consents;
  const partners: Partner[] = request.partners;

  const mesPartners: Partner[] = partners.filter(
    (p) => p.type === PARTNER_TYPES.FHIR && p.subType === 'mes'
  );
  const mesConsents: Consent[] = consents.filter((c) =>
    mesPartners.some((p) => p.id === c.clientId)
  );

  const consentRequests = mesConsents.map(
    (mesConsent) =>
      new Promise((resolve, reject) => {
        mediator
          .request<FHIRPairingResponse>('api:invoke', {
            method: 'GET',
            url: urls.lslApi + 'sharing/mes/getpairing/' + mesConsent.clientId,
          })
          .then((result) => {
            if (result.status === 0) {
              if (result.data.mes_status === 200) {
                mesConsent.scopes = result.data.scopes !== undefined ? result.data.scopes : [];
                mesConsent.scopeDetails = result.data.scopeDetails;
              } else {
                // remove the consent, since its invalid
                consents.splice(
                  consents.findIndex((c) => c.clientId === mesConsent.clientId),
                  1
                );
              }

              resolve(result);
            } else {
              reject(result);
            }
          })
          .catch((err) => {
            log.error(err);

            reject(err);
          });
      })
  );

  Promise.all(consentRequests)
    .then(() => {
      store.dispatch({
        type: LOADED_CONSENTS,
        consents,
      });
    })
    .catch((err) => {
      mediator.publish('error:display', {
        error: {
          code: err.status || 901, // adding a default error when something weird happens and we do not have err/status
          type: ERROR_PARTNERS_CONNECTIONS,
        },
      });
    });
};

const handlePartnerValidateCode = (request: any) => {
  const { code, yearOfBirth } = request;

  const data = {
    code,
    yearOfBirth,
  };

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<ShareCodeRedirectResponse>('api:invoke', {
        method: 'POST',
        url: `${urls.lslApi}sharing/partner/validateCode`,
        data,
      })
      .then((result) => {
        resolve(result);
        return;
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

const handleConsentScreen = (request: any) => {
  const { partnerID, yearOfBirth, code } = request;

  store.dispatch({ type: SET_SHARECODE, code });
  store.dispatch({ type: SET_YEAR_OF_BIRTH, yearOfBirth });

  mediator.publish('router:navigate', '/consent/manage/' + partnerID);
  return;
};

const handlePartnerAcceptConsent = (request: any) => {
  const { code, yearOfBirth } = request;

  const data = {
    code,
    yearOfBirth,
  };

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<{ consent: Consent }>('api:invoke', {
        method: 'POST',
        url: `${urls.lslApi}sharing/partner/${request.partnerId}/acceptConsent`,
        data,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: REMOVE_SHARECODE, code });
          store.dispatch({ type: REMOVE_YEAR_OF_BIRTH, yearOfBirth });
          store.dispatch({ type: ADDED_CONSENT, consent: result.data.consent });
          store.dispatch({ type: REMOVED_PENDING_CONSENT, partnerId: request.partnerId });

          mediator.publish('router:navigate', '/share/' + request.partnerId);

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

const handlePartnerDeclineReconsent = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'DELETE',
        url: `${urls.lslApi}sharing/partner/${request.partnerId}/declineHealthdataScope`,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: REMOVED_CONSENT, partnerId: request.partnerId });

          const state = store.getState();
          const partners = state.partner.partners;
          const connected = state.partner.connected;
          const pendingConnections = state.partner.pendingConnections;

          mediator.publish('partner:navigateOtherConnections', {
            partners,
            connected,
            pending: pendingConnections,
          });

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

// This route doesn't exist yet in lvapi so this is just placeholder until ADC-34065 is ready
const handlePartnerAcceptReconsent = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<{ consent: Consent }>('api:invoke', {
        method: 'POST',
        url: `${urls.lslApi}sharing/partner/${request.partnerId}/acceptHealthdataScope`,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: UPDATED_CONSENT, consent: result.data.consent });

          mediator.publish('router:navigate', '/share/' + request.partnerId);

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

const handlePartnerDeclineConsent = (request: any) => {
  const { code, yearOfBirth } = request;

  const data = {
    code,
  };

  request.response = new Promise((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'POST',
        url: `${urls.lslApi}sharing/partner/${request.partnerId}/declineConsent`,
        data,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: REMOVE_SHARECODE, code });
          store.dispatch({ type: REMOVE_YEAR_OF_BIRTH, yearOfBirth });
          store.dispatch({ type: REMOVED_PENDING_CONSENT, partnerId: request.partnerId });

          const state = store.getState();
          const partners = state.partner.partners;
          const connected = state.partner.connected;
          const pendingConnections = state.partner.pendingConnections;

          mediator.publish('partner:navigateOtherConnections', {
            partners,
            connected,
            pending: pendingConnections,
          });

          resolve(result);
          return;
        }

        reject(result);
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

const handleNavigateOtherConnections = (request: any) => {
  const partners = Object.values(request.partners) as Partner[];
  const displayPartners = partners.some((p) => p.type === PARTNER_TYPES.API && p.display);
  const capiPartners = partners.some((p) => p.type === PARTNER_TYPES.COMMERCIAL_API);

  if (
    request.connected.length > 0 ||
    request.pending.length > 0 ||
    (displayPartners && !capiPartners)
  ) {
    mediator.publish('router:navigate', '/otherConnections');
  } else {
    mediator.publish('router:navigate', '/');
  }
};

const handleInitialUrl = () => {
  const state = store.getState().partner;

  const querystring = (window.location.search.split('?')[1] || '').split('#')[0];
  const query = qs.decode(querystring);

  if (!query.pId) {
    return; // no Partner in query
  }

  const p = state.partners[String(query.pId)];

  if (!p) {
    return;
  }

  if (p.type === PARTNER_TYPES.INBOUND) {
    handleInitialUrlInbound(state, query);

    return;
  }

  if (!query.token) {
    return; // no token in query
  }

  if (p.type === PARTNER_TYPES.COMMERCIAL_API) {
    handleInitialUrlCommercialApi(state, query);

    return;
  }
};

const handleInitialUrlInbound = (state: PartnerState, query: ParsedUrlQuery) => {
  if (query.error) {
    mediator.publish('router:navigate', `/inbound/${query.pId}/error`);

    return;
  }

  if (state.connected.find((c) => c.clientId === query.pId)) {
    mediator.publish('router:navigate', `/share/${query.pId}`);

    return;
  }
};

const handleInitialUrlCommercialApi = (state: PartnerState, query: ParsedUrlQuery) => {
  if (state.connected.find((c) => c.clientId === query.pId)) {
    mediator.publish('router:navigate', `/consent/manage/${query.pId}`);

    return;
  }

  if (state.pendingConnections.find((pc) => pc.clientId === query.pId)) {
    mediator.publish('router:navigate', `/consent/manage/${query.pId}`);

    return;
  }

  mediator.publish('router:navigate', `/invitation`);
};

const handleInitiateInboundConnection = (request: {
  partnerId: string;
  response: Promise<InboundRedirectResponse>;
}) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<InboundRedirectResponse>('api:invoke', {
        method: 'POST',
        url: `${urls.lslApi}sharing/inbound/${request.partnerId}/initiate`,
      })
      .then((result) => {
        if (result.status === 0) {
          window.location.assign(result.data.to);
          resolve(result.data);
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        log.error(err);
        reject(err);
      });
  });
};

mediator.subscribe('partner:getConsents', handleGetConsents);
mediator.subscribe('partner:getPendingConsents', handleGetPendingConsents);
mediator.subscribe('partner:removeConsent', handleRemoveConsent);
mediator.subscribe('partner:removeConsentModal', handleRemoveConsentModal);
mediator.subscribe('partner:consentInfo', handleConsentInfo);
mediator.subscribe('partner:checkConnections', handleCheckConnections);
mediator.subscribe('partner:validateCode', handlePartnerValidateCode);
mediator.subscribe('partner:acceptConsent', handlePartnerAcceptConsent);
mediator.subscribe('partner:declineConsent', handlePartnerDeclineConsent);
mediator.subscribe('partner:acceptReconsent', handlePartnerAcceptReconsent);
mediator.subscribe('partner:declineReconsent', handlePartnerDeclineReconsent);
mediator.subscribe('partner:consentScreen', handleConsentScreen);
mediator.subscribe('partner:navigateOtherConnections', handleNavigateOtherConnections);
mediator.subscribe('partner:initiateInboundConnection', handleInitiateInboundConnection);
mediator.subscribe('partner:errorLoadingModal', handleLoadingError);

mediator.once('store:created', () => {
  observeStore(
    (state) => state.user.id,
    (userId) => {
      if (userId) {
        Promise.all([handleGetConsents(), handleGetPendingConsents()]).then(() => {
          handleInitialUrl();
        });
      }
    }
  );
});
